import { useCallback, useEffect, useRef, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  /** The key `data` was loaded for (it can lag `key` with `keepPrevious`). */
  dataKey: string | null;
  error: unknown;
  loading: boolean;
  reload: () => void;
}

/**
 * Runs `load` when `key` changes (and on `reload`), aborting the previous
 * call. Keeps the last data while a reload is in flight so the screen does
 * not flash.
 */
export function useAsync<T>(
  key: string | null,
  load: (signal: AbortSignal) => Promise<T>,
  opts: { keepPrevious?: boolean } = {},
): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [dataKey, setDataKey] = useState<string | null>(null);
  const keepPrevious = opts.keepPrevious === true;
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(key !== null);
  const [tick, setTick] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (key === null) {
      setLoading(false);
      return;
    }
    if (lastKey.current !== key) {
      lastKey.current = key;
      if (!keepPrevious) {
        setData(null);
        setDataKey(null);
      }
    }
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    loadRef
      .current(ctrl.signal)
      .then((d) => {
        if (!ctrl.signal.aborted) {
          setData(d);
          setDataKey(key);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) {
          setError(e);
          setLoading(false);
        }
      });
    return () => ctrl.abort();
  }, [key, tick, keepPrevious]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { data, dataKey, error, loading, reload };
}

/** Seconds left until `expiresAt`, ticking every second; null without a deadline. */
export function useSecondsLeft(expiresAt: string | null): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAt) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  if (!expiresAt) return null;
  return Math.max(0, (Date.parse(expiresAt) - now) / 1000);
}

/** Calls `fn` every `ms` while `active`, and when the tab becomes visible again. */
export function useRefresh(active: boolean, ms: number, fn: () => void): void {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') ref.current();
    }, ms);
    const onVis = () => {
      if (document.visibilityState === 'visible') ref.current();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [active, ms]);
}

/** Whether the viewport matches a media query. */
export function useMedia(query: string): boolean {
  const get = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [match, setMatch] = useState(get);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const m = window.matchMedia(query);
    const on = () => setMatch(m.matches);
    on();
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, [query]);
  return match;
}
